import { createPortal } from "react-dom";
import { formatActiveNumber } from "../../i18n/formatting";
import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Circle, LockKeyhole, RefreshCw, ShieldCheck } from "lucide-react";
import {
  advancePublishingSetup, getPublishingSetup, SETUP_STEPS, type PublishingSetupPlan,
} from "./collectorPublishingSetupApi";
import { useCollectorReleaseRegistryCopy } from "./collectorReleaseRegistryCatalog";
import { TufMaintenancePolicyEditor } from "./TufMaintenancePolicyEditor";
import "../../styles/collector-publishing-setup.css";

const STEP_COPY = {
  signer: "setup.signer", policy: "setup.policy", keys: "setup.keys",
  root: "setup.root", activation: "setup.activation",
} as const;

export function CollectorPublishingSetup({ onPrepared, actionLabel, scope, onRecheck, recheckBusy, actionBarHost, headingLevel = 2 }: {
  onPrepared?: () => Promise<void>;
  actionLabel?: string;
  onRecheck?: (() => void) | undefined;
  actionBarHost?: HTMLElement | null | undefined;
  recheckBusy?: boolean | undefined;
  scope?: string;
  headingLevel?: 2 | 3 | 4;
}) {
  const { t } = useCollectorReleaseRegistryCopy();
  const [plan, setPlan] = useState<PublishingSetupPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [paused, setPaused] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const mounted = useRef(false);
  const stopRequested = useRef(false);
  const Heading = `h${headingLevel}` as "h2" | "h3" | "h4";

  async function refresh() {
    setLoading(true);
    setError(null);
    setFinished(false);
    setPaused(false);
    try {
      const result = await getPublishingSetup();
      if (mounted.current) setPlan(result);
    } catch (reason) {
      if (mounted.current) setError(reason instanceof Error ? reason.message : "unknown");
    } finally {
      if (mounted.current) setLoading(false);
    }
  }
  useEffect(() => {
    mounted.current = true;
    void refresh();
    return () => { mounted.current = false; };
  }, []);

  async function approve() {
    if (!plan || busy || plan.state === "blocked") return;
    setBusy(true);
    setError(null);
    setPaused(false);
    stopRequested.current = false;
    try {
      // Continue only the server's current plan. Each step commits separately;
      // closing/reopening resumes canonical state and requires a new approval.
      let current = plan;
      if (current.state === "ready" && onPrepared) {
        // Revalidate even an already-ready review: keys or trust may have
        // changed while the page was open or while MFA was being completed.
        current = await advancePublishingSetup(current.revision);
        if (mounted.current) setPlan(current);
        if (current.state !== "ready") throw new Error("publishing_setup_changed");
      }
      for (let remaining = SETUP_STEPS.length + 1; current.state !== "ready"; remaining -= 1) {
        if (!mounted.current || stopRequested.current) return;
        if (remaining <= 0 || current.state === "blocked") throw new Error(current.code ?? "unknown");
        current = await advancePublishingSetup(current.revision);
        if (mounted.current) setPlan(current);
      }
      if (stopRequested.current) return;
      if (mounted.current && onPrepared) {
        setFinalizing(true);
        await onPrepared();
      }
      if (mounted.current) setFinished(true);
    } catch (reason) {
      if (mounted.current) setError(reason instanceof Error ? reason.message : "unknown");
    } finally {
      if (mounted.current) { setBusy(false); setFinalizing(false); }
    }
  }

  const code = error ?? plan?.code;
  const errorCopy = code === "publishing_setup_configuration"
    ? t("setup.configuration")
    : code === "publishing_setup_changed" ? t("setup.changed")
      : code === "publishing_setup_busy" ? t("setup.otherOperation")
        : code && ["existing_trust_requires_review", "root_expired", "signer_ambiguity",
          "authority_requires_review"].includes(code) ? t("setup.reviewRequired")
          : t("setup.retryBody");
  const completed = plan ? SETUP_STEPS.filter(step => plan.steps[step]).length : 0;
  const needsRecheck = Boolean(error) || paused || plan?.state === "blocked";
  const progressCopy = t("setup.progress", { completed: formatActiveNumber(completed), total: formatActiveNumber(SETUP_STEPS.length) });
  const actionBar = <footer className="collector-publishing-action-bar" role="group" aria-label={t("setup.actions")} aria-busy={busy || loading || recheckBusy}>
      <div>
        {busy && !finalizing && <button type="button" disabled={paused} onClick={() => {
          stopRequested.current = true;
          setPaused(true);
        }}>{t("setup.pause")}</button>}
        <button type="button" className={needsRecheck ? "collector-primary" : undefined} disabled={busy || loading || recheckBusy}
          onClick={() => { void refresh().then(() => onRecheck?.()); }}>
          <RefreshCw size={15} aria-hidden="true" />{t("readiness.recheck")}
        </button>
        {plan && plan.state !== "blocked" && !finished && (plan.state !== "ready" || onPrepared) && <button
          type="button" className={needsRecheck ? undefined : "collector-primary"} disabled={busy || loading || paused || Boolean(error)}
          onClick={() => void approve()}
        >{busy ? t("setup.working") : actionLabel ?? t("setup.approve")}</button>}
        {plan?.state === "ready" && !onPrepared && <strong className="collector-publishing-complete">{t("setup.ready")}</strong>}
      </div>
      <span><LockKeyhole size={15} aria-hidden="true" />{t("setup.approval")}</span>
    </footer>;
  return <section className="collector-publishing-setup" aria-label={t("setup.title")} aria-busy={busy || loading}>
    <header>
      <ShieldCheck size={20} aria-hidden="true" />
      <div><Heading>{t("setup.title")}</Heading><p>{scope ?? t("setup.scope")}</p></div>
    </header>
    {actionBarHost ? createPortal(actionBar, actionBarHost) : actionBar}
    {loading && <p role="status">{t("setup.loading")}</p>}
    {code && <div className="collector-publishing-feedback" role="alert" data-kavoosh-inline-validation="true">
      <strong>{t("setup.paused")}</strong><p>{errorCopy}</p>
      <details><summary>{t("add.technicalDetails")}</summary><code dir="ltr">{code}</code></details>
    </div>}
    {paused && <p role="status" className="collector-publishing-paused">{busy ? t("setup.pausedBody") : t("setup.pausedConfirmed")}</p>}
    {finished && <p role="status" className="collector-publishing-complete">{t("setup.complete")}</p>}
    {plan && <>
      <div className="collector-publishing-progress" role="progressbar" aria-label={t("setup.progressLabel")}
        aria-valuemin={0} aria-valuemax={SETUP_STEPS.length} aria-valuenow={completed} aria-valuetext={progressCopy}>
        <strong>{progressCopy}</strong>
        {!paused && !finished && (plan.state !== "ready" || onPrepared) && <span>{busy ? t("setup.working") : t("setup.body")}</span>}
      </div>
      <ol className="collector-publishing-steps">
        {SETUP_STEPS.map((step) => <li key={step} aria-current={busy && plan.next_step === step ? "step" : undefined}>
          {plan.steps[step] ? <CheckCircle2 size={17} aria-hidden="true" /> : <Circle size={17} aria-hidden="true" />}
          <span>{t(STEP_COPY[step])}</span>
          <small>{plan.steps[step] ? t("setup.verified") : t("setup.pending")}</small>
        </li>)}
      </ol>
    </>}
    <p className="collector-publishing-optional">{t("setup.manualOptional")}</p>
    <details className="collector-technical-details" onToggle={(event) => setAdvancedOpen(event.currentTarget.open)}>
      <summary>{t("setup.controls")}</summary>
      <p>{t("setup.policyBody")}</p><p>{t("setup.preserve")}</p>
      {advancedOpen && !busy && <TufMaintenancePolicyEditor compact onSaved={() => { void refresh(); }} />}
    </details>
  </section>;
}
